/**Google Play Store — public account deletion page (mandatory for all apps with accounts).
 *
 * Route: /delete-account
 * Accessible without login (Google Play reviewers must be able to reach it).
 * Submits the user's email + optional reason to POST /api/account-deletion-requests.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle2, ShieldAlert, Trash2, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeleteAccountPage() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/account-deletion-requests`, {
        email: email.trim().toLowerCase(),
        reason: reason.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please email support@misyou.in.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white px-6 py-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold">Request Account Deletion</h1>
          <p className="text-slate-300 mt-3">
            Permanently delete your MISYOU account and all associated personal data.
          </p>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-6 py-8">
        {submitted ? (
          <Card className="border-emerald-200 shadow-sm">
            <CardContent className="pt-8 pb-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Request Received</h2>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Your account deletion request has been received and will be processed within
                <strong> 7 working days</strong>.
              </p>
              <p className="text-slate-500 text-xs">
                Once your account is deleted, all personal data (name, email, phone, photos,
                reported cases, payment history) will be permanently removed in accordance
                with our Privacy Policy and DPDP Act obligations.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Alert className="mb-6 border-amber-200 bg-amber-50">
              <ShieldAlert className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-xs">
                <strong>This action is permanent.</strong> Once deleted, your account, reported
                cases, uploaded photos, and payment history cannot be recovered.
              </AlertDescription>
            </Alert>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-500" /> Delete My Account
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="delete-email" className="text-sm">
                      Registered Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="delete-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="delete-account-email-input"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Enter the email you used to sign up for MISYOU.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="delete-reason" className="text-sm">Reason (optional)</Label>
                    <Textarea
                      id="delete-reason"
                      placeholder="Tell us why you're leaving (optional)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={3}
                      data-testid="delete-account-reason-textarea"
                    />
                  </div>

                  {error && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertDescription className="text-red-700 text-sm">{error}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="destructive"
                    className="w-full"
                    disabled={submitting}
                    data-testid="delete-account-submit-btn"
                  >
                    {submitting ? 'Submitting...' : 'Request Account Deletion'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-6 text-center">
              <p className="text-xs text-slate-400">
                Alternatively, you can email <strong>support@misyou.in</strong> from your registered email
                with the subject <strong>"Account Deletion Request"</strong>.
              </p>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-400">
                <Link to="/privacy" className="underline hover:text-slate-600">Privacy Policy</Link>
                <Link to="/terms" className="underline hover:text-slate-600">Terms of Service</Link>
                <Link to="/" className="underline hover:text-slate-600">Back to Home</Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
